import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Spinner } from '../components/Spinner';

/** Job del pipeline tal como lo entrega la API. */
export interface PipelineJob {
  id: string;
  kind: 'edition' | 'source-test';
  type: 'alerta' | 'boletin' | null;
  status: 'queued' | 'running' | 'done' | 'error';
  stage: string | null;
  detail: Record<string, unknown> | null;
  edition_id: string | null;
  source_id: string | null;
  /** Corrida histórica: día en que se paró Víctor (null = corrida del día). */
  scan_date: string | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  /** Panel compartido: quién la generó (null = corrida programada del cron). */
  generada_por?: string | null;
  schedule_id?: string | null;
}

const CLAVE_JOB = 'ambec-job-activo';
const POLL_MS = 2500;

export const ETAPAS: Record<string, string> = {
  fuentes: 'Descargando las fuentes oficiales',
  N1: 'Nicanor (N1) · leyendo las fuentes',
  N2: 'Marcela (N2) · clasificando y priorizando',
  N3N4: 'Violeta ⇄ Gabriela · redactando y auditando',
  N5: 'Pablo (N5) · componiendo la edición',
  finalizando: 'Componiendo la noticia',
};

interface JobsCtx {
  job: PipelineJob | null;
  /** Empieza a seguir un job (persistente entre páginas y recargas). */
  seguir: (id: string) => void;
  descartar: () => void;
}

const Ctx = createContext<JobsCtx>({ job: null, seguir: () => {}, descartar: () => {} });
export const useJobs = () => useContext(Ctx);

export function JobsProvider({ children }: { children: ReactNode }) {
  const [job, setJob] = useState<PipelineJob | null>(null);
  const [oculto, setOculto] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const detener = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }, []);

  const consultar = useCallback(
    async (id: string) => {
      try {
        const data = await apiFetch<{ job: PipelineJob }>(`/api/newsletter/jobs/${id}`);
        setJob(data.job);
        if (data.job.status === 'done' || data.job.status === 'error') {
          detener();
          localStorage.removeItem(CLAVE_JOB);
        }
      } catch {
        /* red intermitente: el próximo tick reintenta */
      }
    },
    [detener],
  );

  const seguir = useCallback(
    (id: string) => {
      detener();
      setOculto(false);
      localStorage.setItem(CLAVE_JOB, id);
      void consultar(id);
      timer.current = setInterval(() => void consultar(id), POLL_MS);
    },
    [consultar, detener],
  );

  const descartar = useCallback(() => {
    setOculto(true);
    if (job && (job.status === 'done' || job.status === 'error')) {
      setJob(null);
      localStorage.removeItem(CLAVE_JOB);
    }
  }, [job]);

  // Al montar: retoma el job guardado o el último activo del servidor.
  useEffect(() => {
    const guardado = localStorage.getItem(CLAVE_JOB);
    if (guardado) {
      seguir(guardado);
      return detener;
    }
    void (async () => {
      try {
        const data = await apiFetch<{ jobs: PipelineJob[] }>('/api/newsletter/jobs');
        const activo = data.jobs.find(
          (j) => j.kind === 'edition' && (j.status === 'queued' || j.status === 'running'),
        );
        if (activo) seguir(activo.id);
      } catch {
        /* sin jobs que retomar */
      }
    })();
    return detener;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Ctx.Provider value={{ job, seguir, descartar }}>
      {children}
      <JobToast job={job} oculto={oculto} onCerrar={descartar} />
    </Ctx.Provider>
  );
}

/** Toast flotante global: sobrevive a la navegación y notifica al terminar. */
function JobToast({
  job,
  oculto,
  onCerrar,
}: {
  job: PipelineJob | null;
  oculto: boolean;
  onCerrar: () => void;
}) {
  if (!job || oculto || job.kind !== 'edition') return null;

  const corriendo = job.status === 'queued' || job.status === 'running';
  const etiquetaTipo = job.type === 'alerta' ? 'alerta' : 'boletín';
  const sinNovedades = job.status === 'done' && !job.edition_id;
  const n3n4 = job.detail?.n3n4 as { itemActual?: number; totalItems?: number } | undefined;
  const etapa =
    job.status === 'queued'
      ? 'En cola, el orquestador la tomará en segundos'
      : (ETAPAS[job.stage ?? ''] ?? 'Trabajando') +
        (job.stage === 'N3N4' && n3n4?.totalItems ? ` (item ${n3n4.itemActual}/${n3n4.totalItems})` : '');

  return (
    <div
      role="status"
      aria-live="polite"
      className="tarjeta fixed bottom-5 right-5 z-50 w-[340px] p-4 shadow-[0_16px_44px_-16px_rgba(1,15,39,0.28)]"
    >
      <div className="flex items-start gap-3">
        {corriendo ? (
          <Spinner tamano="sm" className="mt-0.5 shrink-0" />
        ) : (
          <span
            aria-hidden="true"
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-medium text-white ${
              job.status === 'done' ? 'bg-emerald-500' : 'bg-red-500'
            }`}
          >
            {job.status === 'done' ? '✓' : '!'}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium leading-tight text-azul-noche">
            {corriendo
              ? `Generando ${etiquetaTipo}…`
              : sinNovedades
                ? 'Sin novedades urgentes'
                : job.status === 'done'
                  ? `Tu ${etiquetaTipo} está ${job.type === 'alerta' ? 'lista' : 'listo'}`
                  : `La generación del ${etiquetaTipo} falló`}
          </p>
          <p className="mt-1 text-xs font-normal leading-snug text-gris">
            {corriendo
              ? etapa
              : sinNovedades
                ? `El equipo revisó las fuentes y ningún item amerita ${etiquetaTipo} — no se generó edición.`
                : job.status === 'done'
                  ? 'La edición quedó en borrador para tu revisión.'
                  : (job.error ?? 'Error desconocido.')}
          </p>
          <div className="mt-2.5 flex gap-2">
            <Link
              to={`/newsletter/corrida/${job.id}`}
              className="inline-flex h-7 items-center rounded-full border border-azul-profundo/30 px-3 text-xs font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/5"
            >
              {corriendo ? 'Ver corrida en vivo' : 'Ver la corrida'}
            </Link>
            {job.status === 'done' && job.edition_id && (
              <Link
                to="/newsletter"
                className="inline-flex h-7 items-center rounded-full bg-azul-profundo px-3 text-xs font-medium text-white transition-colors duration-150 hover:bg-[#003a7c]"
              >
                Ver la noticia
              </Link>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar aviso"
          className="btn-tabla -mr-1 -mt-1 !h-6 !px-1.5 text-gris hover:bg-slate-100 hover:text-azul-noche"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
